import React from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  TopToolbar,
  EditButton,
  ListButton,
  useRecordContext,
} from 'react-admin';
import DeleteWithPasswordButton from '@/components/DeleteWithPasswordButton';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';

const ShowTopToolbar = () => (
  <TopToolbar>
    <EditButton />
    <DeleteWithPasswordButton resource='clients' label='Eliminar' />
    <ListButton />
  </TopToolbar>
);

const ClientNameField = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
        <PersonIcon fontSize='large' />
      </Avatar>
      <Box>
        <Typography variant='h5' component='div' fontWeight='bold'>
          {record.clientName}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          ID: #{record.id}
        </Typography>
      </Box>
    </Box>
  );
};

const ContactInfoField = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <List dense>
      <ListItem>
        <ListItemIcon>
          <EmailIcon color='primary' />
        </ListItemIcon>
        <ListItemText
          primary='Correo Electrónico'
          secondary={
            <Typography
              component='a'
              href={`mailto:${record.clientEmail}`}
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {record.clientEmail}
            </Typography>
          }
        />
      </ListItem>
      <ListItem>
        <ListItemIcon>
          <PhoneIcon color='primary' />
        </ListItemIcon>
        <ListItemText
          primary='Teléfono de Contacto'
          secondary={
            <Typography
              component='a'
              href={`tel:${record.clientPhoneContact}`}
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {record.clientPhoneContact}
            </Typography>
          }
        />
      </ListItem>
    </List>
  );
};

export const RolesField = () => {
  const record = useRecordContext();
  console.log('ClientsShow RolesField - record:', record);
  console.log(
    'ClientsShow RolesField - record.rolePermissions:',
    record?.rolePermissions
  );

  if (!record || !record.rolePermissions) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {record.rolePermissions.length > 0 ? (
        record.rolePermissions.map((rolePermission: any, index: number) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              backgroundColor: 'background.paper',
            }}
          >
            <Chip
              label={rolePermission.role}
              variant='outlined'
              color='primary'
              icon={<SecurityIcon />}
              sx={{
                backgroundColor: 'primary.50',
                borderColor: 'primary.main',
                fontWeight: 'bold',
              }}
            />
            <Chip
              label={rolePermission.permissionLevel}
              variant='filled'
              color={
                rolePermission.permissionLevel === 'ADMIN'
                  ? 'error'
                  : rolePermission.permissionLevel === 'WRITE'
                    ? 'warning'
                    : 'info'
              }
              size='small'
              sx={{ fontWeight: 'bold' }}
            />
          </Box>
        ))
      ) : (
        <Typography variant='body2' color='text.secondary' fontStyle='italic'>
          No hay roles asignados
        </Typography>
      )}
    </Box>
  );
};

const ClientsShow = () => {
  return (
    <Show title='Detalles del Cliente' actions={<ShowTopToolbar />}>
      <Box sx={{ margin: '0 auto', padding: 2 }}>
        <SimpleShowLayout>
          {/* Header con información principal */}
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
            <ClientNameField />
          </Paper>

          {/* Información de Contacto */}
          <Card sx={{ mb: 3, boxShadow: 3 }}>
            <CardHeader
              avatar={
                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                  <EmailIcon />
                </Avatar>
              }
              title={
                <Typography variant='h6' component='div'>
                  Información de Contacto
                </Typography>
              }
              subheader='Datos de contacto del cliente'
            />
            <Divider />
            <CardContent>
              <ContactInfoField />
            </CardContent>
          </Card>

          {/* Roles y Permisos */}
          <Card sx={{ mb: 3, boxShadow: 3 }}>
            <CardHeader
              avatar={
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <SecurityIcon />
                </Avatar>
              }
              title={
                <Typography variant='h6' component='div'>
                  Roles Asignados
                </Typography>
              }
              subheader='Roles y permisos del cliente en el sistema'
            />
            <Divider />
            <CardContent>
              <RolesField />
            </CardContent>
          </Card>
        </SimpleShowLayout>
      </Box>
    </Show>
  );
};

export default ClientsShow;
